// distancing-cowork 의 /task/working/{task_id} endpoint wrapper.
// REACT_APP_AGENT_API_HOST 미설정 시 in-memory mock (개발 fallback).
//
// git 비유: working_store = local working tree (live editing source), V1 (task-editor 백엔드)
// = remote. working_store 는 task_id 생성 후 항상 살아있다 — [저장] = V1 push + working 을
// V1 과 동기화 ("고정"), [원복] = working 을 V1 으로 reset. 둘 다 row 를 지우지 않는다.
//
// 동시 편집은 Codex / Claude Code 의 OS-filesystem 패턴 — `version` (단조 정수) 가 optimistic
// lock 토큰. PUT 시 `expected_version` 송신, mismatch 면 409 + 현재 row 전체 반환.
// 호출자는 WorkingTaskVersionConflictError 를 catch 해 현재 version 을 따라가거나
// sync.applyAgentPatch 로 3-way merge → ConflictBanner.

import type { TaskEditorBlock } from "pages/task-editor/utils/taskEditorUtils";

const HOST = process.env.REACT_APP_AGENT_API_HOST || "";

export interface WorkingTaskDTO {
  task_id: string;
  blocks: TaskEditorBlock[];
  version: number;
  revision: string;
  last_writer: "agent" | "user";
  updated_at: string;
  created_at: string;
}

export class WorkingTaskVersionConflictError extends Error {
  /** 409 응답 본문 — store 의 현재 (다른 writer 가 만든) row 전체. */
  current: WorkingTaskDTO;
  constructor(current: WorkingTaskDTO) {
    super(
      `working_task version mismatch (current version=${current.version}, last_writer=${current.last_writer})`
    );
    this.name = "WorkingTaskVersionConflictError";
    this.current = current;
  }
}

// =========================
// In-memory mock
// =========================

const mockRows = new Map<string, WorkingTaskDTO>();

function mockNow(): string {
  return new Date().toISOString();
}

// 빠른 FNV-1a 64 위 canonical JSON 16-hex — BE `compute_blocks_revision` 와 비트 동일.
// 본 mock 만 자체 계산 — 실서비스는 BE 가 응답에 revision 동봉.
function mockRevision(blocks: TaskEditorBlock[]): string {
  const canonical = canonicalJSON(blocks ?? []);
  const bytes = new TextEncoder().encode(canonical);
  const FNV_OFFSET = BigInt("0xcbf29ce484222325");
  const FNV_PRIME = BigInt("0x100000001b3");
  const MASK = BigInt("0xffffffffffffffff");
  let h = FNV_OFFSET;
  for (let i = 0; i < bytes.length; i++) {
    h = (h ^ BigInt(bytes[i])) & MASK;
    h = (h * FNV_PRIME) & MASK;
  }
  return h.toString(16).padStart(16, "0");
}
function canonicalJSON(value: unknown): string {
  if (value === null || typeof value !== "object") return JSON.stringify(value);
  if (Array.isArray(value)) {
    return "[" + value.map((v) => canonicalJSON(v)).join(",") + "]";
  }
  const obj = value as Record<string, unknown>;
  const keys = Object.keys(obj).sort();
  return (
    "{" +
    keys.map((k) => JSON.stringify(k) + ":" + canonicalJSON(obj[k])).join(",") +
    "}"
  );
}

// =========================
// Public API
// =========================

export async function upsertWorkingTask(
  taskId: string,
  blocks: TaskEditorBlock[],
  expectedVersion: number,
  writer: "agent" | "user" = "user"
): Promise<WorkingTaskDTO> {
  if (!HOST) {
    const existing = mockRows.get(taskId);
    if (existing && existing.version !== expectedVersion) {
      throw new WorkingTaskVersionConflictError(existing);
    }
    if (!existing && expectedVersion !== 0) {
      // row 없는데 update 기대 — conflict (drop)
      const ghost: WorkingTaskDTO = {
        task_id: taskId,
        blocks: [],
        version: 0,
        revision: mockRevision([]),
        last_writer: writer,
        updated_at: mockNow(),
        created_at: mockNow(),
      };
      throw new WorkingTaskVersionConflictError(ghost);
    }
    const now = mockNow();
    const next: WorkingTaskDTO = {
      task_id: taskId,
      blocks,
      version: existing ? existing.version + 1 : 1,
      revision: mockRevision(blocks),
      last_writer: writer,
      updated_at: now,
      created_at: existing?.created_at ?? now,
    };
    mockRows.set(taskId, next);
    return next;
  }
  const res = await fetch(
    `${HOST}/task/working/${encodeURIComponent(taskId)}`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        blocks,
        expected_version: expectedVersion,
        writer,
      }),
    }
  );
  if (res.status === 409) {
    const current = (await res.json()) as WorkingTaskDTO;
    throw new WorkingTaskVersionConflictError(current);
  }
  if (!res.ok) {
    throw new Error(`upsertWorkingTask failed: ${res.status}`);
  }
  return (await res.json()) as WorkingTaskDTO;
}

export async function getWorkingTask(
  taskId: string
): Promise<WorkingTaskDTO | null> {
  if (!HOST) return mockRows.get(taskId) ?? null;
  const res = await fetch(
    `${HOST}/task/working/${encodeURIComponent(taskId)}`,
    { method: "GET" }
  );
  if (res.status === 404) return null;
  if (!res.ok) {
    throw new Error(`getWorkingTask failed: ${res.status}`);
  }
  return (await res.json()) as WorkingTaskDTO;
}

// DELETE /task/working/{task_id} 는 BE 에 존재하나 FE 에선 호출하지 않는다 — working 은 항상
// V1 mirror 로 유지되므로 (위 상단 주석 참조) 삭제 경로가 없다. 운영 툴이 직접 정리할 때만 사용.

// =========================
// 테스트용 — mock state 리셋
// =========================

export function __resetWorkingTaskMockForTests(): void {
  mockRows.clear();
}
